import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Flex } from 'reflexbox';
import PageHeader from '../components/gamePlayComponents/PageHeader';
import SkeletonImage from '../components/common/SkeletonImage';
import { courses } from '../components/courseContent/courseObjects';

const formatValue = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof RegExp) return value.toString();
  if (Array.isArray(value)) return value.join(', ');
  if (React.isValidElement(value)) return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const TextBlock = ({ children, bold = false, caption = false }) => {
  if (children == null || children === '') return null;

  return (
    <Typography
      variant={caption ? 'caption' : 'body2'}
      component="div"
      style={{
        marginBottom: 12,
        whiteSpace: 'pre-wrap',
        fontWeight: bold ? 'bold' : undefined,
      }}
    >
      {children}
    </Typography>
  );
};

const ImageBlock = ({ src, caption }) => {
  if (!src) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <SkeletonImage src={src} alt="" />
      {caption && <TextBlock caption>{caption}</TextBlock>}
    </div>
  );
};

/** Matches CardContainer order: imageN → caption → contentN */
const ImageContentSlots = ({ data, max = 3 }) => {
  if (!data) return null;

  return (
    <>
      {Array.from({ length: max }, (_, i) => {
        const n = i + 1;
        const image = data[`image${n}`];
        const caption = data[`image${n}Caption`];
        const content = data[`content${n}`];
        if (!image && !content && !caption) return null;

        return (
          <React.Fragment key={n}>
            <ImageBlock src={image} caption={caption} />
            <TextBlock>{formatValue(content)}</TextBlock>
          </React.Fragment>
        );
      })}
    </>
  );
};

const QuestionBlock = ({ index, questionKey, question, showMetadata, showAcceptedAnswer, showHint }) => {
  const title = question.displayName || questionKey;
  const locationName = question.location?.name;
  const answer = question.answer;

  return (
    <Card style={{ marginBottom: 24 }} elevation={2}>
      <CardContent>
        <Typography variant="h6" color="primary" gutterBottom>
          {index}. {title}
        </Typography>
        {showMetadata && (
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
            Key: {questionKey}
            {locationName ? ` · Location: ${locationName}` : ''}
            {question.type ? ` · Type: ${question.type}` : ''}
          </Typography>
        )}

        <Typography variant="subtitle1" color="primary" style={{ fontWeight: 'bold', marginBottom: 8 }}>
          Question
        </Typography>
        <ImageContentSlots data={question} />
        <TextBlock bold>{formatValue(question.boldContent)}</TextBlock>
        <TextBlock>{formatValue(question.captionContent)}</TextBlock>
        {showHint && question.hint && (
          <Typography variant="body2" component="div" style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }}>
            <Typography component="span" variant="body2" style={{ fontWeight: 'bold' }}>
              Hint:
            </Typography>{' '}
            {question.hint}
          </Typography>
        )}

        {question.specialContent && (
          <>
            <Typography variant="subtitle1" color="primary" style={{ fontWeight: 'bold', marginTop: 8, marginBottom: 8 }}>
              Options / Cards
            </Typography>
            {question.specialContent.initialCards && (
              <TextBlock>{question.specialContent.initialCards.join(', ')}</TextBlock>
            )}
            {question.specialContent.initialImages?.map((src, i) => (
              <ImageBlock key={i} src={src} />
            ))}
          </>
        )}

        {answer && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="subtitle1" color="secondary" style={{ fontWeight: 'bold', marginBottom: 8 }}>
              Answer
            </Typography>

            {answer.answerInfo && (
              <>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  Where to find the answer
                </Typography>
                <ImageContentSlots data={answer.answerInfo} max={2} />
                <Divider style={{ margin: '16px 0' }} />
              </>
            )}

            <TextBlock bold>{formatValue(answer.heading)}</TextBlock>
            <ImageContentSlots data={answer} />

            {showAcceptedAnswer && answer.answer != null && answer.answer !== '' && (
              <Typography variant="body2" component="div" style={{ marginTop: 8, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                <Typography component="span" variant="body2" style={{ fontWeight: 'bold' }}>
                  Accepted answer:
                </Typography>{' '}
                {formatValue(answer.answer)}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CourseContentReview = () => {
  const { course: courseKey } = useParams();
  const course = courses[courseKey];
  const courseContent = course?.courseContent;
  const [showMetadata, setShowMetadata] = useState(true);
  const [showAcceptedAnswer, setShowAcceptedAnswer] = useState(true);
  const [showHint, setShowHint] = useState(true);

  if (!course || !courseContent) {
    return (
      <>
        <PageHeader goBack title="Course Content Review" />
        <Typography style={{ padding: 16 }}>Course not found: {courseKey}</Typography>
      </>
    );
  }

  const questions = Object.entries(courseContent);

  return (
    <>
      <PageHeader goBack title={course.friendlyCourseName} />
      <div style={{ padding: '0 16px 32px' }}>
        <Card style={{ marginBottom: 16 }} elevation={1}>
          <CardContent style={{ paddingBottom: 8 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              Show
            </Typography>
            <Flex style={{ flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showMetadata}
                    onChange={(e) => setShowMetadata(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Metadata"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showAcceptedAnswer}
                    onChange={(e) => setShowAcceptedAnswer(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Accepted answer"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showHint}
                    onChange={(e) => setShowHint(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Hint"
              />
            </Flex>
          </CardContent>
        </Card>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
          {questions.length} steps · Course key: {courseKey}
        </Typography>
        {questions.map(([key, question], index) => (
          <QuestionBlock
            key={key}
            index={index + 1}
            questionKey={key}
            question={question}
            showMetadata={showMetadata}
            showAcceptedAnswer={showAcceptedAnswer}
            showHint={showHint}
          />
        ))}
      </div>
    </>
  );
};

export default CourseContentReview;
